/**
 * RpcBinaryCodec - binary encode/decode for RPC wire payloads.
 *
 * ## Payload layout
 *
 * ```
 * [uint8:  flags — hasNetSubId(bit0)]
 * [uint8:  objectId byte length]
 * [utf-8   objectId bytes]
 * [uint8:  methodName byte length]
 * [utf-8   methodName bytes]
 * [if hasNetSubId: uint8 len + utf-8 bytes]
 * [args section: sequential typed values per NetPropertyMetadata[]]
 * ```
 *
 * Args are encoded sequentially without a bitmask (all args are always present
 * in a valid RPC call). Encoding follows the same type rules as replication
 * (`ReplicationSerializer`), always with forceAll=true.
 */

import { decodeRpcValue, encodeRpcValue } from './ObjectSerializer.js';
import { BinaryReader, BinaryWriter } from './ReplicationBinaryCodec.js';

const _encoder = new TextEncoder();
const _decoder = new TextDecoder();

import type { NetPropertyMetadata } from '../utils/serialization/decorator.js';

// ---------------------------------------------------------------------------
// Flags
// ---------------------------------------------------------------------------

const FLAG_HAS_NET_SUB_ID = 0x01;

// ---------------------------------------------------------------------------
// Payload encoding
// ---------------------------------------------------------------------------

/**
 * Encode a full RPC binary payload from a pre-encoded args section.
 *
 * @param objectId    NetObjectId of the target actor (`ClassName'UUID'`).
 * @param methodName  Name of the RPC method.
 * @param argsPayload Binary args produced by {@link encodeRpcArgs}.
 * @param netSubId    Optional component sub-identifier for component RPC routing.
 */
export function encodeRpcPayload(
  objectId: string,
  methodName: string,
  argsPayload: Uint8Array,
  netSubId?: string,
): Uint8Array {
  const headerWriter = new BinaryWriter();
  const flags = netSubId ? FLAG_HAS_NET_SUB_ID : 0;
  headerWriter.writeUint8(flags);
  writeShortString(headerWriter, objectId);
  writeShortString(headerWriter, methodName);
  if (netSubId) writeShortString(headerWriter, netSubId);
  const header = headerWriter.getBytes();
  const result = new Uint8Array(header.length + argsPayload.length);
  result.set(header);
  result.set(argsPayload, header.length);
  return result;
}

/**
 * Decode the header of an RPC binary payload.
 *
 * @returns Parsed header fields plus `argsPayload` — the args section as a
 *          `Uint8Array` slice. Pass it to {@link decodeRpcArgs} when ready to
 *          decode arguments.
 */
export function decodeRpcHeader(payload: Uint8Array): {
  objectId: string;
  methodName: string;
  netSubId: string | undefined;
  argsPayload: Uint8Array;
} {
  const reader = new BinaryReader(payload);
  const flags = reader.readUint8();
  const objectId = readShortString(reader);
  const methodName = readShortString(reader);
  const netSubId = (flags & FLAG_HAS_NET_SUB_ID) ? readShortString(reader) : undefined;
  // All header fields are byte-multiples, so bitCursor is always byte-aligned here.
  const argsPayload = payload.subarray(reader.bitCursor >> 3);
  return { objectId, methodName, netSubId, argsPayload };
}

// ---------------------------------------------------------------------------
// Args encoding
// ---------------------------------------------------------------------------

/**
 * Encode RPC arguments to binary using typed `NetPropertyMetadata`.
 * Each arg is encoded sequentially (forceAll, no bitmask).
 *
 * @param args      Argument values in declaration order.
 * @param paramMeta Matching `NetPropertyMetadata` for each parameter.
 */
export function encodeRpcArgs(args: unknown[], paramMeta: NetPropertyMetadata[]): Uint8Array {
  const writer = new BinaryWriter();
  for (let i = 0; i < paramMeta.length; i++) {
    encodeRpcValue(writer, args[i], paramMeta[i]);
  }
  return writer.getBytes();
}

/**
 * Decode RPC arguments from the reader using typed `NetPropertyMetadata`.
 *
 * @param reader    Reader positioned at the start of the args section.
 * @param paramMeta Matching `NetPropertyMetadata` for each parameter.
 */
export function decodeRpcArgs(reader: BinaryReader, paramMeta: NetPropertyMetadata[]): unknown[] {
  const args: unknown[] = [];
  for (const net of paramMeta) {
    args.push(decodeRpcValue(reader, null, net));
  }
  return args;
}

// ---------------------------------------------------------------------------
// Short string helpers (uint8 length prefix — max 255 bytes)
// ---------------------------------------------------------------------------

function writeShortString(writer: BinaryWriter, str: string): void {
  const bytes = _encoder.encode(str);
  const len = Math.min(bytes.byteLength, 255);
  writer.writeUint8(len);
  for (let i = 0; i < len; i++) {
    writer.writeUint8(bytes[i]);
  }
}

function readShortString(reader: BinaryReader): string {
  const len = reader.readUint8();
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = reader.readUint8();
  }
  return _decoder.decode(bytes);
}
